/**
 * Turnstile HTML Template Generator
 *
 * Generates WebView HTML content for React Native platforms.
 * Extracted from TurnstileWidget.tsx (Line Count Refactor 2025-11-21)
 */

import type { TurnstileWidgetProps } from './turnstileConfig';

/**
 * Generate HTML content for Turnstile WebView
 *
 * Creates a complete HTML document with embedded Turnstile widget.
 * Handles communication with React Native WebView via postMessage.
 *
 * @param siteKey - Cloudflare Turnstile site key
 * @param props - Widget configuration (theme, size, appearance, etc.)
 * @returns Complete HTML document as string
 *
 * @example
 * ```tsx
 * const html = generateTurnstileHtml(siteKey, {
 *   theme: 'dark',
 *   size: 'compact',
 *   appearance: 'always',
 * });
 * <WebView source={{ html }} />
 * ```
 */
export function generateTurnstileHtml(
  siteKey: string,
  {
    theme = 'auto',
    size = 'flexible',
    appearance = 'interaction-only',
    execution = 'render',
    refreshExpired = 'auto',
    retry = 'auto',
    language,
    action = 'analyze_message',
    cData,
  }: Partial<TurnstileWidgetProps>
): string {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Turnstile</title>
  <script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>
  <style>
    body {
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100px;
      background: transparent;
    }
    #turnstile-container {
      transform: scale(0.85);
      transform-origin: center center;
    }
  </style>
</head>
<body>
  <div id="turnstile-container"></div>

  <script>
    // React Native WebView postMessage
    function sendMessage(data) {
      const message = JSON.stringify(data);
      window.ReactNativeWebView.postMessage(message);
    }

    // Callback functions for Turnstile
    function onTurnstileSuccess(token) {
      sendMessage({
        type: 'success',
        token: token
      });
    }

    function onTurnstileError(error) {
      sendMessage({
        type: 'error',
        error: error || 'Unknown error'
      });
    }

    function onTurnstileExpire() {
      sendMessage({
        type: 'expire'
      });
    }

    function onTurnstileTimeout() {
      sendMessage({
        type: 'timeout'
      });
    }

    // Render Turnstile widget when script loads
    window.addEventListener('load', function() {
      try {
        if (typeof turnstile === 'undefined') {
          throw new Error('Turnstile script not loaded');
        }

        // Store widget ID for reset support (Phase 2)
        // Reset is triggered via injectJavaScript from React Native, which
        // runs directly in this context — no postMessage listener needed.
        window.__turnstileWidgetId = turnstile.render('#turnstile-container', {
          sitekey: ${JSON.stringify(siteKey)},
          callback: onTurnstileSuccess,
          'error-callback': onTurnstileError,
          'expired-callback': onTurnstileExpire,
          'timeout-callback': onTurnstileTimeout,
          theme: ${JSON.stringify(theme)},
          size: ${JSON.stringify(size)},
          appearance: ${JSON.stringify(appearance)},
          execution: ${JSON.stringify(execution)},
          'refresh-expired': ${JSON.stringify(refreshExpired)},
          retry: ${JSON.stringify(retry)},
          ${language ? `language: ${JSON.stringify(language)},` : ''}
          action: ${JSON.stringify(action)},
          ${cData ? `cData: ${JSON.stringify(cData)},` : ''}
        });

        sendMessage({
          type: 'ready'
        });
      } catch (error) {
        onTurnstileError(error.message);
      }
    });
  </script>
</body>
</html>
  `;
}
